import Cookies from "js-cookie";
import { login } from "@/services/authService";
import { User, LoginResponse } from "@/models/user.model";
import { Router } from "vue-router";
import userService from "@/services/userService";
import { toRoleName } from "@/constants/Roles";

type State = {
  token: string | null;
  user: User | null;
};

const loadUserFromStorage = (): User | null => {
  const stored = localStorage.getItem("user");
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem("user");
      return null;
    }
  }
  return null;
};

const state: State = {
  token: Cookies.get("auth-token") || null,
  user: loadUserFromStorage(),
};

const mutations = {
  setAuth(state: State, response: LoginResponse) {
    const normalizedRole = toRoleName(response.role) || response.role;
    state.token = response.token;
    state.user = {
      email: response.email,
      name: response.name,
      role: normalizedRole,
      dealerId: response.dealerId,
    };
    Cookies.set("auth-token", response.token, { sameSite: "Strict" });
    localStorage.setItem("user", JSON.stringify(state.user));
  },

  setCurrentUser(state: State, user: User | null) {
    state.user = user;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      return;
    }
    localStorage.removeItem("user");
  },

  clearAuth(state: State) {
    state.token = null;
    state.user = null;
    Cookies.remove("auth-token");
    localStorage.removeItem("user");
  },
};

const getters = {
  isAuthenticated: (state: State) => !!state.token,
  currentUser: (state: State) => state.user,
  userName: (state: State) => state.user?.name || "",
  userEmail: (state: State) => state.user?.email || "",
  userRole: (state: State) => state.user?.role || "",
};

const actions = {
  async login(
    { commit, dispatch }: { commit: any; dispatch: any },
    { email, password }: { email: string; password: string }
  ) {
    try {
      const response = await login({ email, password });
      commit("setAuth", response);
      await dispatch("loadMyProfile");
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async loadMyProfile(
    { state, commit, dispatch }: { state: State; commit: any; dispatch: any },
    options: { notifyOnError?: boolean } = {}
  ) {
    const token = state.token || Cookies.get("auth-token");
    if (!token) return null;

    try {
      const profile = await userService.getMyProfile();
      const currentUser: User = {
        email: profile.email,
        name: profile.name,
        role: profile.role,
        dealerId: profile.dealerId,
      };
      commit("setCurrentUser", currentUser);
      return currentUser;
    } catch (error: any) {
      if (options.notifyOnError) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      return null;
    }
  },

  logout({ commit, dispatch }: { commit: any; dispatch: any }, router?: Router) {
    commit("clearAuth");
    dispatch("dealers/clearMyDealer", null, { root: true });
    if (router) {
      router.push({ name: "UserLogin" });
    }
  },

  checkExpiration({ state, commit }: { state: State; commit: any }) {
    const token = state.token || Cookies.get("auth-token");
    if (!token) return false;

    try {
      const parts = token.split(".");
      if (parts.length < 2) return false;

      const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload = payloadBase64.padEnd(
        payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4),
        "="
      );
      const payload = JSON.parse(atob(paddedPayload));
      const exp = typeof payload?.exp === "number" ? payload.exp : null;

      if (!exp) return false;
      const isExpired = Date.now() >= exp * 1000;
      if (isExpired) {
        commit("clearAuth");
      }
      return isExpired;
    } catch {
      return false;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
